// --------------------------
// IMPORTS
// --------------------------
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import TableContent from "../../components/atoms/TableContent";
import {
  getRoleListThunk,
  getPermissionsThunk,
  addRoleThunk,
  deleteRoleThunk,
} from "../../store/thunks/rolesThunk";
import TopContainerHeader from "../../components/molecules/TopComponentHeader";
import { Form } from "antd";
import useCurrentPath, {
  deFaultHandlePaginationChange,
  formateDateTime,
  handleDelete,
} from "../../utils/Constants";

// --------------------------
// MAIN COMPONENT
// --------------------------
const Roles = () => {
  //Dispatch + Custom Hooks
  const dispatch = useDispatch();
  const currentPath = useCurrentPath();
  const [form] = Form.useForm();

  // --------------------------
  // REDUX STATE
  // --------------------------
  const storeRole = useSelector((state) => state.auth.role);
  const storeLIUId = useSelector((state) => state.auth.lIUId);
  const roles = useSelector((state) => state.roles.list) || [];
  const rolesLoading = useSelector((state) => state.roles.loading);
  const storePageInfo = useSelector((state) => state.roles.pageInfo);

  // --------------------------
  // LOCAL STATES
  // --------------------------
  const [showLoader, setShowLoader] = useState(true);
  const [storeTotalCount, setStoreTotalCount] = useState(0);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [permissions, setPermissions] = useState([]);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [filteredRoles, setFilteredRoles] = useState([]);
  const [searchText, setSearchText] = useState("");

  // --------------------------
  // API CALLS
  // --------------------------

  // Fetch roles and permissions
  const fetchRoles = async () => {
    const res = await dispatch(
      getRoleListThunk({
        route: currentPath,
        pagination: {
          current: 1,
          pageSize: process.env.REACT_APP_DEFAULT_PAGINATION_PAGESIZE,
        },
      })
    ).unwrap();

    setStoreTotalCount(res.totalCount || 0);
    setShowLoader(false);
  };

  // --------------------------
  // USE EFFECTS
  // --------------------------
  useEffect(() => {
    fetchRoles();

    dispatch(getPermissionsThunk())
      .unwrap()
      .then((res) => {
        setPermissions(res || []); // update local state
      })
      .catch((err) => console.error(err));
  }, [currentPath, dispatch]);

  useEffect(() => {
    setFilteredRoles(roles); // sync on data change
  }, [roles]);

  // --------------------------
  // TABLE COLUMNS
  // --------------------------
  const columns = [
    ...(storeRole === process.env.REACT_APP_SROLE
      ? [
          {
            title: "Sr. No.",

            render: (text, record, index) => index + 1,
          },
        ]
      : []),
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Permissions",
      dataIndex: "permissions",
      key: "permissions",
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) => text && formateDateTime(text),
    },
  ];

  // Row selection configuration
  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedRowKeys(selectedRowKeys);
    },
    getCheckboxProps: (record) => ({
      disabled: record.name === "Disabled User",
      name: record.name,
    }),
  };

  // --------------------------
  // FORM FIELDS
  // --------------------------
  const formFields = [
    {
      isVisible: true,
      label: "Name",
      name: "name",
      rules: [
        {
          required: true,
          message: "Please input name",
        },
      ],
      placeholder: "Enter the name",
      type: "input",
    },
    {
      isVisible: true,
      label: "Permissions",
      name: "permissions",
      rules: [
        {
          required: true,
          message: "Please enter permission",
        },
      ],
      size: "large",
      className: "rounded-md h-45px rounded-md",
      allowClear: "true",
      placeholder: "Select Permission",
      type: "select",
      options: permissions,
      mode: "multiple",
      onchnge: true,
    },
  ];

  // --------------------------
  // JSX RETURN
  // --------------------------
  return (
    <div className="container">
      <div className="bgi-no-repeat card-stretch gutter-b rounded-lg p-0">
        <TopContainerHeader
          sectionNameClass="col-lg-7 text-left commonTitleText"
          sectionTitle="Roles"
          sectionDescription={
            showLoader ? "Loading..." : `${storeTotalCount} Found`
          }
          hasSectionButton={false}
          hasSearch={true}
          hasSearchButton="general"
          addNewButtonClassName="btn mx-auto px-5 py-2 defaultbutton rounded-lg"
          name="Add New Role"
          searchClassName="table-search"
          searchPlaceholder="Search Role's Name"
          onButtonClick={() => setIsAddModalOpen(true)}
          searchData={roles}
          onFilteredData={setFilteredRoles}
          searchKeys={["name"]}
          addFormModalOpen={isAddModalOpen}
          addFormSetIsModalOpen={setIsAddModalOpen}
          addFormModalShowLoader={setShowLoader}
          payload={(values) => ({
            route: currentPath,
            name: values?.name, // values comes from form submit
            permissions: JSON.stringify(values?.permissions),
            createdAt: new Date().toISOString(),
            historySource: "backend",
            historyAction: "create",
            historyUserID: storeLIUId,
          })}
          list={addRoleThunk}
          formFields={formFields}
          successMessage="Role added successfully."
          onSuccess={fetchRoles}
          selectedRowKeys={
            storeRole === process.env.REACT_APP_SROLE && selectedRowKeys
          }
          deleteOnClick={() => {
            handleDelete({
              selectedRowKeys,
              dispatch,
              deleteThunk: deleteRoleThunk,
              route: currentPath,
              actionName: "role",
              onSuccess: () => setSelectedRowKeys([]),
            });
          }}
          searchText={searchText}
          setSearchText={(value) => {
            setShowLoader(true);
            setSearchText(value);
            setTimeout(() => setShowLoader(false), 500);
          }}
        />
        <div
          className={`card p-5 rounded-lg paddingTop-0 ${
            storeTotalCount <= process.env.REACT_APP_DEFAULT_PAGINATION_PAGESIZE
              ? "paddingBottom-0"
              : "minHeight-100vh"
          }`}
        >
          <div className="table-area">
            <div className="table-responsive">
              <div className="tab-content" id="alltable-size">
                <TableContent
                  bordered={false}
                  columns={columns}
                  rowSelection={rowSelection}
                  storeList={filteredRoles}
                  pagination={storePageInfo}
                  loading={showLoader}
                  totalCount={storeTotalCount}
                  handlePaginationChange={(page, pageSize) =>
                    deFaultHandlePaginationChange(
                      (type, pagination) =>
                        dispatch(
                          getRoleListThunk({ route: currentPath, pagination })
                        ),
                      page,
                      pageSize,
                      storeTotalCount
                    )
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Roles;
